import React, { useState } from "react";
import Button from "Components/DesignSystem/Button/Button";
import Menu from "Components/DesignSystem/Menu/Menu";
import { MenuItem } from "types/DesignSystem/Menu";

type ActionProps = {
    onOpenImportModal: () => void;
    onOpenValidateModal: () => void;
    onExport: () => void;
    disabled: boolean;
    openAttributeMenu: () => void;
    isReadOnly?: boolean;
}

/**
 * The "Actions" dropdown on the item editor/details header, on DS Button + Menu
 * (§ 82). Menu's `onDismiss` owns Escape and outside-click; a picked item closes
 * the menu itself, a disabled one leaves it open — both the legacy behaviors.
 */
export const Actions = ({ onOpenImportModal, onOpenValidateModal, onExport, disabled, openAttributeMenu, isReadOnly = false }: ActionProps) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);

    const pick = (callback: () => void) => {
        setShowMenu(false);
        callback();
    };

    const items: MenuItem[] = [
        {
            id: 'import-excel', label: 'Import from Excel',
            disabled: disabled || isReadOnly,
            'data-testid': 'lists-import-excel-btn',
            onClick: () => pick(onOpenImportModal),
        },
        {
            id: 'import-attribute', label: 'Import from Attribute',
            disabled: disabled || isReadOnly,
            'data-testid': 'lists-import-attribute-btn',
            onClick: () => pick(openAttributeMenu),
        },
        /* Gated on `isReadOnly` ALONE — not `disabled ||` like the two Imports.
           Pinned legacy asymmetry (okf/list/e2e.md § 7.1); do not "fix" it here. */
        {
            id: 'validate-excel', label: 'Validate Excel',
            disabled: isReadOnly,
            'data-testid': 'lists-validate-excel-btn',
            onClick: () => pick(onOpenValidateModal),
        },
        {
            id: 'download', label: 'Download',
            'data-testid': 'lists-download-btn',
            onClick: () => pick(onExport),
        },
    ];

    return (
        <div className="tw-relative tw-inline-block tw-z-50">
            <Button
                variant="subtle"
                data-testid="lists-actions-btn"
                aria-haspopup="menu"
                aria-expanded={showMenu}
                onClick={() => setShowMenu((prev) => !prev)}
            >
                Actions
            </Button>
            {showMenu ? (
                <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-48">
                    <Menu
                        items={items}
                        data-testid="lists-actions-menu"
                        onDismiss={() => setShowMenu(false)}
                    />
                </div>
            ) : null}
        </div>
    )
}
